import * as Sentry from '@sentry/nextjs';
import {
  use,
  type ChangeEvent,
  type Dispatch,
  type SetStateAction,
} from 'react';

import { useApolloClient } from '@apollo/client/react';

import { env } from '@/env';

import { CurrentUserContext } from '@/context/CurrentUserContext';

import { getRestApiUrl } from '@/utils/getRestApiUrl';

import { useTrackIntercomEvent } from '@/hooks/useTrackIntercomEvent';

import type {
  FileManagerUploadingState,
  UploadingZipStatus,
} from '../components/file-manager/FileManagerUploadingBar';
import { FILE_MANAGER_QUERY } from '../components/FileManager';

import type { Segment } from '../components/file-manager/toolbar/breadcrumbs-bar/FileManagerBreadcrumbsBarSegment';

import { useCreateFolder } from './useCreateFolder';

interface Props {
  segment: Segment;
  setUploadingState: Dispatch<SetStateAction<FileManagerUploadingState>>;
  rootOwnerId: string;
}

export const useZipUploader = ({
  segment,
  setUploadingState,
  rootOwnerId,
}: Props) => {
  const client = useApolloClient();
  const { currentUserId } = use(CurrentUserContext);
  const trackIntercomEvent = useTrackIntercomEvent();

  const [createFolderMutation] = useCreateFolder(rootOwnerId);

  const createFolder = async (opts: {
    owner: string;
    ownerType: string;
    parent: string | null | undefined;
    hidden: boolean;
    name: string;
  }): Promise<string> => {
    const { owner, ownerType, parent, name } = opts;

    // There's a bug in the file service (called in the api service via @ortoo/upload) where it tries to create an empty folder with no name
    if (!name) {
      return '';
    }

    const { data } = await createFolderMutation({
      variables: {
        data: {
          filename: name,
          owner,
          parent: parent ?? null,
          type: ownerType,
        },
      },
    });

    return data?.createFolder?.file?._id ?? '';
  };

  const createOrUpdateFile = async (file: {
    parent: string | null | undefined;
  }) => {
    await client.query({
      query: FILE_MANAGER_QUERY,
      variables: {
        fileId: file.parent ?? null,
        rootOwnerId: segment.ownerId ?? '',
        includeSharingGroups: false,
      },
      fetchPolicy: 'network-only',
    });
  };

  const generateZipUploader = async () => {
    const { ZipUploader } = await import('@ortoo/upload');

    const zipUploader = new ZipUploader({
      apiEndpoint: getRestApiUrl(),
      fileUploadUrl: env.NEXT_PUBLIC_FILE_UPLOAD_URL,
      fileUploadBucket: env.NEXT_PUBLIC_FILE_UPLOAD_BUCKET,
      awsFileUploadAccessKey: env.NEXT_PUBLIC_AWS_FILE_UPLOAD_ACCESS_KEY,
      accessToken: () => '',
      cookieAuth: true,
      createFolder,
      createOrUpdateFile,
    });

    zipUploader.on('submitted', (id: number, filename: string) => {
      const file = zipUploader.getFile(id);

      // Now the file properties won't be set at this point, due to the way
      // the underlying fineuploader works, so we'll do the next bit asynchronously
      setTimeout(() => {
        const parent = zipUploader._fileParams[id]?.parent;

        const newZipFolder = {
          id,
          filename,
          uploadedBytes: 0,
          totalBytes: file.size,
          parent,
          startedAt: new Date(),
        };

        setUploadingState((prevState) => ({
          ...prevState,
          uploadingZipFiles: [...prevState.uploadingZipFiles, newZipFolder],
        }));
      });
    });

    zipUploader.on(
      'progress',
      (
        id: number,
        _filename: string,
        uploadedBytes: number,
        totalBytes: number,
      ) => {
        setUploadingState((prevState) => {
          const uploadingZipFiles = prevState.uploadingZipFiles.map(
            (zipFolder) => {
              if (zipFolder.id === id) {
                return {
                  ...zipFolder,
                  uploadedBytes,
                  totalBytes,
                };
              }

              return zipFolder;
            },
          );

          return {
            ...prevState,
            uploadingZipFiles,
          };
        });
      },
    );

    zipUploader.on(
      'complete',
      (id: number, _filename: string, res: { error?: string }) => {
        const file = zipUploader.getFile(id);
        if (res.error) {
          setUploadingState((prevState) => {
            const uploadingZipFiles = prevState.uploadingZipFiles.map(
              (file) => {
                if (file.id === id) {
                  return {
                    ...file,
                    error: res.error,
                  };
                }

                return file;
              },
            );

            return {
              ...prevState,
              uploadingZipFiles,
            };
          });
        } else {
          const owner = segment?.ownerId ?? '';
          const ownerType = segment?.ownerType ?? '';
          trackIntercomEvent(
            `Uploaded ${ownerType}${file.type === 'zip' ? ' Zip' : ''} File`,
            {
              groupId: ownerType === 'Group' ? owner : undefined,
              serviceId: ownerType === 'Service' ? owner : undefined,
              fileName: _filename,
            },
          );
        }
      },
    );

    zipUploader.on('error', (id: number, filename: string, error: string) => {
      Sentry.captureException(new Error(error), {
        user: currentUserId ? { id: currentUserId } : undefined,
        tags: { uploadType: 'zip' },
        extra: {
          rootOwnerId,
          ownerId: segment?.ownerId,
          ownerType: segment?.ownerType,
          parentId: zipUploader._fileParams[id]?.parent ?? segment?.fileId,
        },
      });

      setUploadingState((prevState) => {
        const uploadingZipFiles = prevState.uploadingZipFiles.map((file) => {
          if (file.id === id) {
            return {
              ...file,
              error,
            };
          }

          return file;
        });

        return {
          ...prevState,
          uploadingZipFiles,
        };
      });
    });
    zipUploader.on(
      'status',
      (id: number, _filename: string, status: UploadingZipStatus) => {
        setUploadingState((prevState) => {
          const uploadingZipFiles = prevState.uploadingZipFiles.map(
            (zipFolder) => {
              if (zipFolder.id === id) {
                return {
                  ...zipFolder,
                  status,
                };
              }

              return zipFolder;
            },
          );

          return {
            ...prevState,
            uploadingZipFiles,
          };
        });
      },
    );

    return zipUploader;
  };

  const uploadZip = async (file: File) => {
    const zipUploader = await generateZipUploader();

    zipUploader.uploadFiles([file], {
      owner: segment.ownerId ?? undefined,
      ownerType: segment.ownerType,
      hidden: false,
      parent: segment.fileId ?? null,
    });
  };

  const handleUploadRequest = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.currentTarget?.files) {
      return;
    }

    const file = event.currentTarget.files[0];

    await uploadZip(file);
  };

  return { handleUploadRequest };
};
